import { useState } from 'react';
import { Luggage as LuggageIcon, CheckCircle2, AlertTriangle } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import { post } from '../../api/client';
import { Card, Button, Badge, StatusBadge, Loader, EmptyState, SectionTitle, Modal, Field, Textarea } from '../../components/UI';
import { fmtDateTime } from '../../utils/format';
import { NotIncluded } from './shared';

export default function AttendeeLuggage() {
  const { eventId, hasModule } = useEvents();
  const toast = useToast();
  const included = hasModule('onboardingLuggage') || hasModule('arrivalLuggage');
  const { data: bags, loading, reload } = useFetch('/luggage/my', { event: eventId }, { skip: !eventId || !included });
  const [missingFor, setMissingFor] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null);

  if (!included) return <><SectionTitle title="Luggage" /><NotIncluded what="Luggage handling" icon={LuggageIcon} /></>;
  if (loading) return <Loader full label="Loading your bags" />;

  const confirm = async (bag) => {
    setBusy(bag._id);
    try { const res = await post(`/luggage/${bag._id}/confirm`, {}); toast.success(res.message); reload(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const reportMissing = async () => {
    setBusy(missingFor._id);
    try {
      const res = await post(`/luggage/${missingFor._id}/report-missing`, { note });
      toast.success(res.message); setMissingFor(null); setNote(''); reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  return (
    <>
      <SectionTitle title="Luggage" subtitle="Your tagged bags and where they are" />
      {!bags?.length ? (
        <EmptyState message="No bags tagged yet" hint="Bags tagged at the luggage desk appear here with their tag and delivery status." icon={LuggageIcon} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {bags.map((b) => {
            const last = b.timeline?.[b.timeline.length - 1];
            return (
              <Card key={b._id} padding={20}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--gold-300)', letterSpacing: 0.8 }}>{b.tagNumber}</p>
                  <StatusBadge status={b.status} />
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 5, textTransform: 'capitalize' }}>
                  {[b.bagType, b.color].filter(Boolean).join(' · ')}
                </p>

                {b.qrImage && (
                  <div style={{ textAlign: 'center', marginTop: 14 }}>
                    <div style={{ display: 'inline-block', padding: 9, background: '#fff', borderRadius: 'var(--r)' }}>
                      <img src={b.qrImage} alt={`Tag ${b.tagNumber}`} style={{ width: 128, height: 128, display: 'block' }} />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 12, fontSize: 12.8, color: 'var(--text-mid)', display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span>Room: <strong style={{ color: 'var(--text-hi)' }}>{b.deliveredRoomNumber || b.room?.roomNumber || 'Not allocated yet'}</strong></span>
                  {last && <span style={{ color: 'var(--text-lo)' }}>Last update {fmtDateTime(last.at)}{last.note ? ` — ${last.note}` : ''}</span>}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                  {b.confirmedByUser ? (
                    <Badge tone="success"><CheckCircle2 size={10} /> Received</Badge>
                  ) : (
                    <>
                      {b.status === 'delivered' && (
                        <Button size="sm" loading={busy === b._id} onClick={() => confirm(b)}>I have my bag</Button>
                      )}
                      {b.status !== 'missing' && (
                        <Button size="sm" variant="subtle" icon={AlertTriangle} onClick={() => setMissingFor(b)}>Report missing</Button>
                      )}
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(missingFor)} onClose={() => setMissingFor(null)}
        title="Report a missing bag" subtitle={missingFor?.tagNumber}
        footer={<><Button variant="subtle" onClick={() => setMissingFor(null)}>Cancel</Button><Button onClick={reportMissing} loading={busy === missingFor?._id}>Report</Button></>}
      >
        <Field label="What should we know?" hint="Where you last saw it, anything distinctive">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Navy suitcase with a red ribbon…" />
        </Field>
      </Modal>
    </>
  );
}
